/* eslint-disable no-plusplus */
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { ReadOnlyEditor } from '@doltech/ui/lib/figma/DolEditor/DolEditorReadOnly';
import { BlogEditorProps } from '@doltech/ui/lib/figma/BlogEditor/BlogEditor.types';
import styled from 'styled-components';
import { sharedFontStyles } from '@doltech/ui/lib/figma/DolEditor/font-styles';
import { tableStyle } from '@doltech/ui/lib/figma/DolEditor/elements/table-blog/components/readonly/styles/blog';
import { blockQuoteStyle } from '@doltech/ui/lib/figma/BlogEditor/plugins/Quote/styles/blog';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import {
  useBlogEditorReadOnlyGrammarPlugin,
} from '../../core-blog-editor/hooks/useBlogEditorReadOnlyGrammarPlugin';
import { useMount } from 'react-use';
import { isEmpty } from 'lodash';
import {
  EditorExerciseMultipleChoicePlugin
} from '@doltech/ui/lib/editor-save-data/multiple-choice/EditorExerciseMultipleChoicePlugin';
import {
  EditorExerciseGapFillWithHintPlugin
} from '@doltech/ui/lib/editor-save-data/gap-fill-with-hint/EditorExerciseGapFillWithHintPlugin';
import { EditorExerciseQuizPlugin } from '@doltech/ui/lib/editor-save-data/quiz/EditorExerciseQuizPlugin';
import { EditorExerciseShortAnswerPlugin } from '@doltech/ui/lib/editor-save-data/short-answer/EditorExerciseShortAnswerPlugin';
import { EditorExerciseGapFillPlugin } from '@doltech/ui/lib/editor-save-data/gap-fill/EditorExerciseGapFillPlugin';

const exerciseTypeList = ['exercise-multiple-choice', 'exercise-gap-fill', 'exercise-short-answer', 'exercise-gap-fill-with-hint', 'exercise-quiz'];

const ReadOnlyEditorStyled = styled(ReadOnlyEditor)`
  ${sharedFontStyles}
  ${tableStyle}
  ${blockQuoteStyle}
  p,
  h1,
  h2,
  h3 {
    margin-bottom: 0;
  }

  a {
    cursor: pointer;
    color: ${colorsV2.blue100};
    text-decoration-line: underline;

    &:hover {
      color: ${colorsV2.blue60};
      text-decoration-line: underline;
    }
  }

  > * + * {
    margin-top: 24px;
  }

  > * + .admin-table-container {
    margin-bottom: 40px;
  }

  > * + h1 {
    margin-top: 16px;
  }

  > * + h2 {
    margin-top: 12px;
  }

  > * + h3 {
    margin-top: 18px;
  }

  > p + p {
    margin-top: 32px;
  }

  // bullet point

  ul,
  ol {
    li {
      &:not(:last-child) {
        margin-bottom: 8px;
      }

      p {
        margin-bottom: 0;

        + ul,
        ol {
          margin-top: 4px;
        }
      }
    }
  }

  font-family: Inter, sans-serif;
`;

export const GrammarBlogEditorReadOnlyV2 = observer(
  React.forwardRef((props: BlogEditorProps, ref) => {
    const {
      initialValue,
      onBackWhenInComplete,
      onMounted,
      plugins: overridePlugins,
      onCheckAnswer,
      onNeedToSignIn,
      progressData,
    } = props;

    const [progressList, setProgressList] = React.useState(progressData || []);

    useMount(() => {
      if (!isEmpty(progressData)) {
        setProgressList(progressData);
      }
    });

    const { plugins, preprocessReadOnlyInitialValues } =
      useBlogEditorReadOnlyGrammarPlugin({ onBackWhenInComplete });


    const preprocessedInitialValue = React.useMemo(() => {
      return preprocessReadOnlyInitialValues({ initialValue });
    }, [preprocessReadOnlyInitialValues, initialValue]);

    const listExerciseType = preprocessedInitialValue?.filter(item => {
      return exerciseTypeList?.includes(item?.type);
    });

    const handleCheckLastAnswer = (payload) => {
      let isCompleted = false;
      const totalExercises = listExerciseType?.length;
      const totalAnswers = progressList?.length;
      if (!progressList?.find(item => item?.exerciseId === payload?.exerciseId)) {
        if (totalAnswers + 1 === totalExercises) {
          isCompleted = true;
        }
      }
      setProgressList(prevProgressList => {
        if (prevProgressList?.find(item => item?.exerciseId === payload?.exerciseId)) {
          return prevProgressList?.map(obj => {
            if (payload?.exerciseId === obj?.questionId) {
              return payload;
            }
            return obj;
          });
        }
        return [...prevProgressList, payload];
      });
      onCheckAnswer?.(payload, isCompleted);

    };

    // grammar
    return (
      <ReadOnlyEditorStyled
        initialValue={preprocessedInitialValue}
        plugins={overridePlugins || [
          ...plugins,
          EditorExerciseMultipleChoicePlugin({
            isReadOnly: true,
            onBackWhenInComplete,
            progressList: progressData || [],
            onCheckAnswer: (payload) => handleCheckLastAnswer(payload),
            onNeedToSignIn,
            color: 'purple',
          }),
          EditorExerciseGapFillWithHintPlugin({
            isReadOnly: true,
            onBackWhenInComplete,
            progressList: progressData || [],
            onCheckAnswer: (payload) => handleCheckLastAnswer(payload),
            onNeedToSignIn,
            color: 'purple',
          }),
          EditorExerciseQuizPlugin({
            isReadOnly: true,
            onBackWhenInComplete,
            progressList: progressData || [],
            onCheckAnswer: (payload) => handleCheckLastAnswer(payload),
            onNeedToSignIn,
            color: 'purple',
          }),
          EditorExerciseShortAnswerPlugin({
            isReadOnly: true,
            onBackWhenInComplete,
            progressList: progressData || [],
            onCheckAnswer: (payload) => handleCheckLastAnswer(payload),
            onNeedToSignIn,
            color: 'purple',
          }),
          EditorExerciseGapFillPlugin({
            isReadOnly: true,
            onBackWhenInComplete,
            progressList: progressData || [],
            onCheckAnswer: (payload) => handleCheckLastAnswer(payload),
            onNeedToSignIn,
            color: 'purple',
          }),
        ]}
        onMounted={onMounted}
      />
    );
  }),
);
